import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { Empty, Tooltip } from 'antd';

export function AuditTrend({ buckets = [], onSelect, result }) {
    const host = useRef(null);
    useEffect(() => {
        if (!host.current || !buckets.length) return undefined;
        const chart = echarts.init(host.current);
        const failedOnly = result === 'failure', successOnly = result === 'success';
        chart.setOption({
            animation: false, color: ['#1677ff', '#d4380d'],
            tooltip: { trigger: 'axis', renderMode: 'richText' },
            legend: { top: 0 }, grid: { left: 48, right: 16, top: 36, bottom: 68 },
            xAxis: { type: 'category', data: buckets.map(b => dayjs(b.started_at).format('MM-DD HH:mm')), axisLabel: { hideOverlap: true } },
            yAxis: { type: 'value', minInterval: 1 },
            dataZoom: [{ type: 'slider', bottom: 12, height: 22, filterMode: 'none' }],
            series: [
                ...(!failedOnly ? [{ name: '成功', type: 'bar', stack: 'total', data: buckets.map(b => b.total - b.failed) }] : []),
                ...(!successOnly ? [{ name: '失败', type: 'bar', stack: 'total', itemStyle: { color: '#d4380d' }, data: buckets.map(b => b.failed) }] : []),
            ],
        });
        chart.on('click', event => { const b = buckets[event.dataIndex]; if (b) onSelect({ from: b.started_at, to: b.ended_at }); });
        chart.on('datazoom', event => {
            const zoom = event.batch?.[0] || event;
            const first = Math.max(0, Math.floor((zoom.start || 0) / 100 * (buckets.length - 1)));
            const last = Math.min(buckets.length - 1, Math.ceil((zoom.end ?? 100) / 100 * (buckets.length - 1)));
            onSelect({ from: buckets[first].started_at, to: buckets[last].ended_at });
        });
        const observer = new ResizeObserver(() => chart.resize()); observer.observe(host.current);
        return () => { observer.disconnect(); chart.dispose(); };
    }, [buckets, onSelect, result]);
    return buckets.length ? <div ref={host} className="audit-trend" role="img" aria-label="成功与失败操作趋势，可点击时间柱或拖动底部滑块筛选" /> : <Empty description="暂无趋势数据" />;
}
export function AuditHeatmap({ cells = [], granularity, scope, onSelect, result }) {
    const max = Math.max(1, ...cells.map(cell => cell.total));
    const days = new Map();
    for (const cell of cells) {
        const date = dayjs(cell.started_at).format('MM-DD');
        if (!days.has(date)) days.set(date, []);
        days.get(date).push(cell);
    }
    const cellButton = cell => {
        const selected = scope?.from === cell.started_at && scope?.to === cell.ended_at;
        const red = result === 'failure' || (!result && cell.failed > cell.total - cell.failed);
        const alpha = cell.total ? 0.2 + 0.8 * cell.total / max : 0.05;
        const rgb = red ? [212, 56, 13] : [9, 88, 217];
        const luminance = rgb.map(c => (255 + (c - 255) * alpha) / 255).map(c => c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4).reduce((sum, c, i) => sum + c * [0.2126, 0.7152, 0.0722][i], 0);
        const title = `${dayjs(cell.started_at).format('MM-DD HH:mm')} — ${dayjs(cell.ended_at).format('MM-DD HH:mm')} · ${cell.total} 次 / ${cell.failed} 次失败`;
        return <Tooltip key={cell.started_at} title={title}>
            <button type="button" aria-label={title} aria-pressed={selected} className={selected ? 'selected' : ''}
                style={{ background: `rgba(${rgb.join(',')},${alpha})`, color: luminance > 0.179 ? '#000' : '#fff' }}
                onClick={() => onSelect(selected ? null : { from: cell.started_at, to: cell.ended_at })}>
                {granularity === 'day' ? dayjs(cell.started_at).format('MM-DD') : dayjs(cell.started_at).format('HH')}
            </button>
        </Tooltip>;
    };
    if (granularity === 'day') return <div className="audit-heatmap audit-heatmap-days" aria-label="登录活动热力图">{cells.map(cellButton)}</div>;
    return <div className="audit-hourly-map" aria-label="登录活动热力图">{[...days].map(([date, entries]) => <div className="audit-day-row" key={date}><span className="audit-date-label">{date}</span><div className="audit-heatmap audit-hour-row">{entries.map(cellButton)}</div></div>)}</div>;
}
